import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Download } from "lucide-react";
import { motion } from "framer-motion";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useTransactions } from "@/hooks/queries";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { parseLocalDate } from "@/lib/dates";

const Reports = () => {
  const { data: transactions = [], isLoading } = useTransactions();
  const { formatCurrency } = useUserPreferences();
  const { toast } = useToast();

  const getMonthlyReport = () => {
    const monthlyData: { [key: string]: { month: string; income: number; expenses: number; net: number } } = {};

    transactions.forEach((t) => {
      const monthKey = format(parseLocalDate(t.date), "MMM yyyy");
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthKey, income: 0, expenses: 0, net: 0 };
      }
      if (t.type === "income") {
        monthlyData[monthKey].income += Number(t.amount);
      } else {
        monthlyData[monthKey].expenses += Number(t.amount);
      }
      monthlyData[monthKey].net = monthlyData[monthKey].income - monthlyData[monthKey].expenses;
    });

    return Object.values(monthlyData).slice(-12);
  };

  const getCurrentMonthStats = () => {
    const now = new Date();
    const monthStart = format(startOfMonth(now), "yyyy-MM-dd");
    const monthEnd = format(endOfMonth(now), "yyyy-MM-dd");

    const currentMonth = transactions.filter(
      (t) => t.date >= monthStart && t.date <= monthEnd
    );

    const income = currentMonth.filter((t) => t.type === "income").reduce((sum, t) => sum + Number(t.amount), 0);
    const expenses = currentMonth.filter((t) => t.type === "expense").reduce((sum, t) => sum + Number(t.amount), 0);

    return { income, expenses, net: income - expenses };
  };

  const getLastMonthStats = () => {
    const lastMonth = subMonths(new Date(), 1);
    const monthStart = format(startOfMonth(lastMonth), "yyyy-MM-dd");
    const monthEnd = format(endOfMonth(lastMonth), "yyyy-MM-dd");

    const lastMonthData = transactions.filter(
      (t) => t.date >= monthStart && t.date <= monthEnd
    );

    const income = lastMonthData.filter((t) => t.type === "income").reduce((sum, t) => sum + Number(t.amount), 0);
    const expenses = lastMonthData.filter((t) => t.type === "expense").reduce((sum, t) => sum + Number(t.amount), 0);

    return { income, expenses, net: income - expenses };
  };

  const exportToCSV = () => {
    const csvContent = [
      ["Date", "Type", "Category", "Amount"],
      ...transactions.map((t) => [t.date, t.type, t.category, t.amount.toString()]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();

    toast({
      title: "Success",
      description: "Report exported successfully",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const monthlyData = getMonthlyReport();
  const currentMonth = getCurrentMonthStats();
  const lastMonth = getLastMonthStats();

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Financial Reports</h1>
            <p className="text-muted-foreground">Comprehensive analysis of your finances</p>
          </div>
          <Button onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Current Month</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Income</span>
                <span className="text-lg font-semibold text-income-text">{formatCurrency(currentMonth.income)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Expenses</span>
                <span className="text-lg font-semibold text-expense-text">{formatCurrency(currentMonth.expenses)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-medium">Net</span>
                <span className={`text-xl font-bold ${currentMonth.net >= 0 ? "text-income-text" : "text-expense-text"}`}>
                  {formatCurrency(currentMonth.net)}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Last Month</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Income</span>
                <span className="text-lg font-semibold text-income-text">{formatCurrency(lastMonth.income)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Expenses</span>
                <span className="text-lg font-semibold text-expense-text">{formatCurrency(lastMonth.expenses)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-medium">Net</span>
                <span className={`text-xl font-bold ${lastMonth.net >= 0 ? "text-income-text" : "text-expense-text"}`}>
                  {formatCurrency(lastMonth.net)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>12-Month Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="income" fill="hsl(var(--income))" name="Income" />
                <Bar dataKey="expenses" fill="hsl(var(--expense))" name="Expenses" />
                <Bar dataKey="net" fill="hsl(var(--primary))" name="Net" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Income Change</span>
                  <span className={`text-sm font-medium ${currentMonth.income - lastMonth.income >= 0 ? "text-income-text" : "text-expense-text"}`}>
                    {currentMonth.income - lastMonth.income >= 0 ? "+" : ""}
                    {formatCurrency(currentMonth.income - lastMonth.income)}
                    {" "}
                    ({lastMonth.income > 0 ? ((currentMonth.income - lastMonth.income) / lastMonth.income * 100).toFixed(1) : "0"}%)
                  </span>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Expense Change</span>
                  <span className={`text-sm font-medium ${currentMonth.expenses - lastMonth.expenses >= 0 ? "text-expense-text" : "text-income-text"}`}>
                    {currentMonth.expenses - lastMonth.expenses >= 0 ? "+" : ""}
                    {formatCurrency(currentMonth.expenses - lastMonth.expenses)}
                    {" "}
                    ({lastMonth.expenses > 0 ? ((currentMonth.expenses - lastMonth.expenses) / lastMonth.expenses * 100).toFixed(1) : "0"}%)
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Reports;